import { useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../../../components/Card";
import { Checkbox } from "../../../components/Checkbox";
import { Button } from "../../../components/CTA";
import { Drawer } from "../../../components/Drawer";
import { Dropdown } from "../../../components/Dropdown";
import { Flex } from "../../../components/Flex";
import { H2, H3 } from "../../../components/Heading";
import Loading from "../../../components/Loading";
import { Text } from "../../../components/Text";
import { View } from "../../../components/View";
import { MOBILE_BREAKPOINT } from "../../../config";
import {
  useAccountsQuery,
  useAddBrandToGroupMutation,
  useRemoveBrandFromGroupMutation,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import { actions } from "../../../store/auth/slice";
import styled from "../../../styles";

export const GroupBrands = () => {
  const client = useGqlClient();
  const {
    data: accountQuery,
    isLoading,
    isFetching,
  } = useAccountsQuery(client);
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const { mutateAsync: addBrandToGroup, isLoading: isAddingBrandToGroup } =
    useAddBrandToGroupMutation(client);
  const { mutate: removeBrandFromGroup, isLoading: isRemovingBrandFromGroup } =
    useRemoveBrandFromGroupMutation(client);
  const queryClient = useQueryClient();

  if (!activeGroupId) {
    return <Text>Something went wrong</Text>;
  }

  const handleAddBrands = () => {
    const promises = selectedBrands.map((brandId) =>
      addBrandToGroup({
        brandId,
        brandGroupId: activeGroupId,
      })
    );

    Promise.all(promises)
      .then(() => {
        toast.success(
          selectedBrands.length === 1
            ? "Brand added to group"
            : "Brands added to group"
        );
        queryClient.invalidateQueries(["Accounts"], {
          exact: false,
        });
        setSelectedBrands([]);
        setIsOpen(false);
      })
      .catch(() => {
        toast.error("Failed to add brands to group");
      });
  };

  if (
    isLoading ||
    isFetching ||
    !accountQuery ||
    isAddingBrandToGroup ||
    isRemovingBrandFromGroup
  )
    return <Loading />;

  const groupBrands =
    accountQuery.account?.brands
      ?.filter((brand) => brand.brandGroup?.id === activeGroupId)
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  const nonGroupBrands =
    accountQuery.account?.brands
      ?.filter((brand) => !brand.brandGroup)
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedBrands([]);
        }}
        showCloseButton={false}
      >
        <DrawerContent>
          <DrawerHeader>
            <H2 margin="xs 0 0">Add Existing Brands</H2>
          </DrawerHeader>
          <DrawerBody>
            {nonGroupBrands.length > 0 ? (
              <Checkbox
                label=""
                options={nonGroupBrands.map((brand) => ({
                  label: brand.name,
                  value: brand.id,
                  isChecked: selectedBrands.includes(brand.id),
                }))}
                onChange={(options) =>
                  setSelectedBrands(
                    options.filter((o) => o.isChecked).map((o) => o.value)
                  )
                }
              />
            ) : (
              <Text margin="0 0 0" colorPreset="secondary" size="s">
                All your existing brands are already in this group
              </Text>
            )}
          </DrawerBody>
          <DrawerFooter>
            <Button
              size="s"
              buttonType="secondary"
              onClick={() => {
                setIsOpen(false);
                setSelectedBrands([]);
              }}
              margin="0 s 0 0"
            >
              Cancel
            </Button>
            <Button
              size="s"
              buttonType="primary"
              onClick={handleAddBrands}
              disabled={selectedBrands.length === 0}
            >
              Add{" "}
              {selectedBrands.length > 0 ? `(${selectedBrands.length})` : ""}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <StyledCard>
        <Flex justify="space-between" align="flex-start" margin="0 0 xs">
          <View>
            <H3 margin="0 0 0">Manage Brands</H3>
            <Text margin="0 0 0" colorPreset="secondary">
              Add or remove brands from your group
            </Text>
          </View>
          <div>
            <Button
              size="s"
              buttonType="secondary"
              onClick={() => {
                setIsOpen(true);
              }}
            >
              Add Brands
            </Button>
          </div>
        </Flex>
        {groupBrands.map((brand) => {
          return (
            <BrandItem
              brandId={brand.id}
              brandName={brand.name}
              brandGroupId={brand.brandGroup?.id}
              activeGroupId={activeGroupId}
              onRemove={() => {
                removeBrandFromGroup(
                  {
                    brandId: brand.id,
                  },
                  {
                    onSuccess: () => {
                      toast.success("Brand removed from group");
                      queryClient.invalidateQueries(["Accounts"], {
                        exact: false,
                      });
                    },
                  }
                );
              }}
            />
          );
        })}
      </StyledCard>
    </>
  );
};

const Row = styled(Flex)`
  justify-content: space-between;
  position: relative;
  padding: ${(p) => p.theme.spacing.s} 0;
  border-bottom: 1px solid ${(p) => p.theme.color.card.divider};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    .trash-icon {
      transform: translateX(0);
      opacity: 1;
    }
    .pending-text {
      transform: translateX(-32px); // Adjust this value as needed
    }
  }
`;

interface BrandItemProps {
  brandId: string;
  brandName: string;
  brandGroupId?: string;
  activeGroupId: string;
  onRemove: () => void;
}

function BrandItem(props: BrandItemProps) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const dispatch = useDispatch();

  return (
    <Row margin="0 0 0 0" justify="space-between" align="center">
      <div>
        <Text margin="0" isCompact weight="bold">
          {props.brandName}
        </Text>
      </div>
      <Dropdown
        margin="0"
        size="xs"
        dropdownType="actions"
        selectionMode="single"
        renderLabel={() => {
          return "Actions";
        }}
        width={180}
        anchor="bottom end"
        selectedOptions={[]}
        setSelectedOptions={(selectedOptions) => {
          const selectedValue = selectedOptions[0];

          if (selectedValue === "remove") {
            props.onRemove();
          } else if (selectedValue === "view") {
            dispatch(actions.setActiveBrand({ brandId: props.brandId }));
            dispatch(
              actions.setActiveGroup({
                groupId: "",
              })
            );
            const brandQueryKeys = [
              queryKeys.myBookings,
              queryKeys.brandLocations,
              queryKeys.brandListings,
              queryKeys.brandBookings,
              queryKeys.reports,
              queryKeys.getBrand,
              queryKeys.accounts,
              queryKeys.billing,
              queryKeys.brandContent,
              queryKeys.accounts,
              queryKeys.brandNotificationCount,
            ];

            brandQueryKeys.forEach((key) => {
              queryClient.resetQueries([key], {
                exact: false,
              });
            });

            history.push("/b");
          }
        }}
        options={[
          {
            label: "Remove from Group",
            value: "remove",
          },
          {
            label: "View Brand",
            value: "view",
          },
        ]}
      />
    </Row>
  );
}

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.l} 0;
  box-sizing: border-box;
`;

const DrawerHeader = styled.div`
  flex-shrink: 0;
  margin-bottom: ${(p) => p.theme.spacing.s};
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* Important for flex child to be scrollable */
`;

const DrawerFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: ${(p) => p.theme.spacing.l} 0;
`;

const StyledCard = styled(Card)`
  padding: ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.l}
    ${(p) => p.theme.spacing.l};
  overflow: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.l};
  }
`;
